import React from 'react'
import { useGamePhase } from '@/hooks/useGamePhase'
import { useAppSelector } from '@/redux/hooks'
import {
  ClockIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { PHASE_LABELS } from '@/constants/Timeline'
import { TimerDisplay } from '../settings/timer-configuration/TimerDisplay'

const GamePhaseInfo: React.FC = () => {
  const charterEnabled = useAppSelector(
    (state) => state.room.currentRoom?.charter_enabled === true
  )
  const charterPhase = useAppSelector((state) => state.charter.session?.phase)
  const charterRoundStatus = useAppSelector(
    (state) => state.charter.session?.round_status
  )

  const {
    currentPhase,
    currentRound,
    totalRounds,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase,
    isMultiRound,
    phaseStatus,
    manualMode,
    isInfinitePhase,
    endTime,
    roundStatus,
  } = useGamePhase()

  const getPhaseIcon = () => {
    if (isRandRPhase)
      return <DocumentTextIcon className='w-5 h-5 text-blue-500' />
    if (isAdvisorPhase)
      return <ChatBubbleLeftRightIcon className='w-5 h-5 text-indigo-500' />
    if (isNegotiationPhase)
      return <ArrowPathIcon className='w-5 h-5 text-amber-500' />
    if (isDecisionPhase)
      return <PencilSquareIcon className='w-5 h-5 text-red-500' />
    return <ClockIcon className='w-5 h-5 text-gray-500' />
  }

  const getPhaseName = () => {
    if (isRandRPhase) return PHASE_LABELS.randr
    if (isAdvisorPhase) return PHASE_LABELS.advisor
    if (isNegotiationPhase) return PHASE_LABELS.negotiation
    if (isDecisionPhase) return PHASE_LABELS.decision
    return 'Waiting for Game to Start'
  }

  const renderStatusCard = (message: string) => (
    <div className='flex items-center justify-between p-3 bg-white rounded-md shadow'>
      <div className='flex items-center gap-2'>
        <ClockIcon className='w-5 h-5 text-gray-500' />
        <div>
          <h3 className='text-sm font-semibold text-gray-900'>{message}</h3>
        </div>
      </div>
    </div>
  )

  if (charterEnabled && charterPhase === 'phase1_prep' && charterRoundStatus !== 'not_started') {
    return renderStatusCard('Individual Prep — Phase 1')
  }
  if (charterEnabled && charterPhase === 'phase2_group' && charterRoundStatus !== 'not_started') {
    return renderStatusCard('Group Charter Session — Phase 2')
  }

  if (!currentPhase && currentRound === 0) {
    return renderStatusCard('Waiting for Game to Start')
  }

  return (
    <div className='flex items-center justify-between p-3 bg-white rounded-md shadow'>
      <div className='flex items-center gap-2'>
        {getPhaseIcon()}
        <div>
          {!manualMode && (
            <h3 className='text-sm font-semibold text-gray-900'>
              {getPhaseName()}
            </h3>
          )}
          {isMultiRound && (
            <p className='text-xs text-gray-500'>
              {manualMode
                ? `Round ${currentRound}`
                : `Round ${currentRound} of ${totalRounds}`}
            </p>
          )}
        </div>
      </div>
      <TimerDisplay
        manualMode={manualMode}
        roundStatus={roundStatus}
        isInfinitePhase={isInfinitePhase}
        endTime={endTime}
        phaseStatus={phaseStatus}
      />
    </div>
  )
}

export default GamePhaseInfo
